/**
 * Ethical aura categories for UI — from backend/categories.json
 * (copied to ./categories.json; keep in sync when the backend file changes).
 *
 * Categories are scoped per artefact via applicable_artifact_ids, which match
 * personas.json route_number. visitor_facing_subtitle is shown in the aura legend.
 */

import categoriesData from './categories.json';
import categoriesElData from './categories_el.json';

const CATEGORIES = Array.isArray(categoriesData) ? categoriesData : [];
const CATEGORIES_EL =
  categoriesElData && typeof categoriesElData === 'object' ? categoriesElData : {};

/** Fallback persona key → route_number when personas bundle is not passed (personas.json). */
const PERSONA_ROUTE_FALLBACK = {
  'Artificial Womb': 1,
  'Face Scanner': 2,
  'Medical Oracle': 3,
  'Probiotic Architecture': 4,
  'Neuro Implants': 5,
  'Cellular Regeneration': 6,
  'Afterlife Archive': 7,
};

const POLE_PALETTE = [
  '#F4C2C2', '#FFD6CC', '#B8E6D8', '#D4B8E6', '#FFF4CC', '#C8E6D4',
  '#C8D4F0', '#E6F0D4', '#D4E0F0', '#B8E0E0', '#FFE6D4', '#E0CCE6',
  '#FFCCD4', '#FFD4E0', '#E8D4F0', '#D4F0E8', '#F0E6D4', '#D4E8F0',
  '#F0D4E8', '#E6E8D4', '#D8E6F4', '#F4E6D4', '#E6D4F4', '#D4F4E6',
];

/** @deprecated Kept for imports that expect a version field. */
export const ETHICAL_DIMENSIONS_VERSION = 3;

const LEGACY_POLE_TO_AURA = {
  Autonomy: 'Self-Determination',
  'Individual Autonomy': 'Self-Determination',
  'System Control': 'Self-Determination',
  'Human Rights': 'Self-Determination',
  'Human-Centered Rights': 'Self-Determination',
  Privacy: 'Privacy and Opacity',
  Visibility: 'Privacy and Opacity',
  Transparency: 'Privacy and Opacity',
  Care: 'Relational Care',
  Efficiency: 'Relational Care',
  Embodiment: 'Embodied Integrity',
  'Technological Mediation': 'Technological Mediation',
  Innovation: 'Technological Mediation',
  Artificiality: 'Technological Mediation',
  Equality: 'Justice and Equity',
  Stratification: 'Justice and Equity',
  'Egalitarian Justice': 'Justice and Equity',
  'Equity-Based Justice': 'Justice and Equity',
  'Human Judgment': 'Situated Judgement',
  'Machine Authority': 'Situated Judgement',
  Knowledge: 'Right to an Open Future',
  Uncertainty: 'Right to an Open Future',
  'Risk-permissive Ethics': 'Right to an Open Future',
  Prevention: 'Precautionary Responsibility',
  Acceptance: 'Precautionary Responsibility',
  Precaution: 'Precautionary Responsibility',
  'Non-maleficence': 'Precautionary Responsibility',
  Naturalness: 'Ecological Interdependence',
  'Rights of Nature': 'Ecological Interdependence',
  'Collective Good': 'Collective Responsibility',
  'Collective Duties': 'Collective Responsibility',
  Communitarianism: 'Collective Responsibility',
  Responsibility: 'Responsibility and Accountability',
  'Diffusion of Accountability': 'Responsibility and Accountability',
};

function allCategoryLabels() {
  return CATEGORIES.map((c) => String(c.aura_ingredient || '').trim()).filter(Boolean).sort();
}

const ALL_CATEGORY_LABELS = allCategoryLabels();
const KNOWN_CATEGORY_SET = new Set(ALL_CATEGORY_LABELS);

export const POLE_COLORS = ALL_CATEGORY_LABELS.reduce((acc, label, index) => {
  acc[label] = POLE_PALETTE[index % POLE_PALETTE.length];
  return acc;
}, {});

/**
 * Resolve route_number for a persona key using personas from GET /api/personas when available.
 */
export function routeNumberForArtefact(artefactName, personas = null) {
  if (!artefactName) return null;
  const key = String(artefactName).trim();
  if (personas && typeof personas === 'object') {
    const p = personas[key];
    if (p && p.route_number != null && p.route_number !== '') {
      const num = typeof p.route_number === 'number'
        ? p.route_number
        : parseInt(String(p.route_number).trim(), 10);
      if (Number.isFinite(num) && num > 0) return num;
    }
    const lowered = key.toLowerCase();
    for (const name of Object.keys(personas)) {
      if (name.toLowerCase() === lowered) {
        const rn = personas[name]?.route_number;
        const num = typeof rn === 'number' ? rn : parseInt(String(rn ?? '').trim(), 10);
        if (Number.isFinite(num) && num > 0) return num;
      }
    }
  }
  if (PERSONA_ROUTE_FALLBACK[key] != null) return PERSONA_ROUTE_FALLBACK[key];
  const lowered = key.toLowerCase();
  for (const [name, rn] of Object.entries(PERSONA_ROUTE_FALLBACK)) {
    if (name.toLowerCase() === lowered) return rn;
  }
  return null;
}

export function getCategoriesForArtifactId(artifactId) {
  if (!artifactId) return [];
  return CATEGORIES.filter(
    (cat) => (cat.applicable_artifact_ids || []).includes(artifactId)
  );
}

export function getCategoriesForArtefact(artefactName, personas = null) {
  const rn = routeNumberForArtefact(artefactName, personas);
  if (rn == null) return [];
  return getCategoriesForArtifactId(rn);
}

/** Category labels applicable to this artefact (radar, legend, filters). */
export function getPolesForArtefact(artefactName, personas = null) {
  return getCategoriesForArtefact(artefactName, personas).map((c) => c.aura_ingredient);
}

export function isKnownPoleLabel(category) {
  const key = String(category ?? '').trim();
  return key.length > 0 && KNOWN_CATEGORY_SET.has(key);
}

/** Map aura_ingredient or legacy pole / DB strings to a current category label. */
export function normalizePoleLabel(category) {
  const key = String(category ?? '').trim();
  if (!key) return null;
  if (KNOWN_CATEGORY_SET.has(key)) return key;
  const mapped = LEGACY_POLE_TO_AURA[key];
  if (mapped && KNOWN_CATEGORY_SET.has(mapped)) return mapped;
  return null;
}

/** @deprecated Alias for normalizePoleLabel */
export function normalizeArtefactKey(artefactName) {
  if (!artefactName || typeof artefactName !== 'string') return null;
  return artefactName.trim();
}

function normalizeNameForMatch(value) {
  if (typeof value !== 'string') return '';
  return value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Resolve per-artefact count bucket from API payload (persona keys may differ slightly). */
export function findArtefactCategoryBucket(byArtefact, artefactName) {
  if (!byArtefact || typeof byArtefact !== 'object' || !artefactName) return null;
  if (byArtefact[artefactName] && typeof byArtefact[artefactName] === 'object') {
    return byArtefact[artefactName];
  }
  const targetNorm = normalizeNameForMatch(artefactName);
  const matchedKey = Object.keys(byArtefact).find((key) => normalizeNameForMatch(key) === targetNorm);
  return matchedKey && typeof byArtefact[matchedKey] === 'object' ? byArtefact[matchedKey] : null;
}

/** Sum legacy/v2 category keys into current aura category counts. */
export function normalizeCategoryCountMap(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.entries(raw).forEach(([category, count]) => {
    const label = normalizePoleLabel(category);
    const n = Number(count);
    if (!label || !Number.isFinite(n) || n <= 0) return;
    out[label] = (out[label] || 0) + n;
  });
  return out;
}

export function isPoleValidForArtefact(category, artefactName, personas = null) {
  if (!isKnownPoleLabel(category)) return false;
  const allowed = getPolesForArtefact(artefactName, personas);
  return allowed.includes(String(category).trim());
}

/** Drop legacy / unknown category keys from count maps. */
export function filterCategoryCountsToKnownPoles(counts) {
  if (!counts || typeof counts !== 'object') return {};
  const out = {};
  Object.entries(counts).forEach(([category, count]) => {
    if (isKnownPoleLabel(category)) out[category] = count;
  });
  return out;
}

/** Keep only categories defined for this artefact. */
export function filterCategoryCountsForArtefact(counts, artefactName, personas = null) {
  const allowed = new Set(getPolesForArtefact(artefactName, personas));
  if (!counts || typeof counts !== 'object' || allowed.size === 0) return {};
  const out = {};
  Object.entries(counts).forEach(([category, count]) => {
    if (allowed.has(category)) out[category] = count;
  });
  return out;
}

export function filterClassificationsForArtefact(items, artefactName, personas = null) {
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => item && isPoleValidForArtefact(item.category, artefactName, personas)
  );
}

export function getColorForCategory(category) {
  const label = normalizePoleLabel(category);
  if (!label) return null;
  return POLE_COLORS[label] || null;
}

function greekCategoryEntry(poleLabel) {
  const label = normalizePoleLabel(poleLabel);
  if (!label) return null;
  const entry = CATEGORIES_EL[label];
  return entry && typeof entry === 'object' ? entry : null;
}

/** Display name for aura legend (English key → localized label). */
export function getPoleDisplayName(poleLabel, locale = 'en') {
  const label = normalizePoleLabel(poleLabel);
  if (!label) return '';
  if (String(locale || '').toLowerCase().startsWith('el')) {
    const el = greekCategoryEntry(label);
    if (el?.aura_ingredient) return String(el.aura_ingredient).trim();
  }
  return label;
}

/** visitor_facing_subtitle for aura legend (from categories.json / categories_el.json). */
export function getPoleDescription(poleLabel, locale = 'en') {
  const label = normalizePoleLabel(poleLabel);
  if (!label) return '';
  if (String(locale || '').toLowerCase().startsWith('el')) {
    const el = greekCategoryEntry(label);
    if (el?.visitor_facing_subtitle) return String(el.visitor_facing_subtitle).trim();
  }
  const cat = CATEGORIES.find((c) => c.aura_ingredient === label);
  return cat ? String(cat.visitor_facing_subtitle || '').trim() : '';
}
